import { useTrackers } from '@ps/web-shared-analytics';
import { StarsButton } from '@ps/web-shared-components';
import classNames from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';

import type { CasinoIcon } from '@swf/casino';
import { CasinoAllButton, CasinoSliderButtons, MoreIcon } from '@swf/casino';
import type { SliderHandles, State as SliderState } from '@swf/components/Slider/Slider';
import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import { select } from '@swf/core/store';

import { TXTINS_Next } from './CasinoGameHeader.messages.json';
import styles from './CasinoGameHeader.scss';
import CasinoHeaderTitle from './components/CasinoHeaderTitle/CasinoHeaderTitle';
import type { Props as TitleProps } from './components/CasinoHeaderTitle/CasinoHeaderTitle';

interface Props {
  active?: number;
  icon?: CasinoIcon;
  className?: string;
  moreLink?: string;
  title?: string;
  subTitle?: string;
  sliderRef?: React.RefObject<SliderHandles>;
  sliderState?: SliderState;
  titleVariant?: TitleProps['variant'];
  hasNextButton?: boolean;
  onNextButtonClick?: () => void;
}

const CasinoGameHeader: React.FC<Props> = ({
  className,
  titleVariant,
  moreLink,
  title,
  subTitle,
  icon,
  sliderRef,
  sliderState,
  hasNextButton,
  onNextButtonClick
}) => {
  const isMobile = useSelector(select.device.isMobile);
  const showScroll = !isMobile && sliderRef && icon && !['icnHeroWide'].includes(icon);
  const { formatMessage } = useRegulatoryIntl();
  const { buttonPressed } = useTrackers();
  const buttonName = formatMessage(TXTINS_Next);

  return !(title || moreLink) && !showScroll ? null : (
    <header
      className={classNames(
        styles.header,
        { [styles.noIcon]: !icon, [styles.right]: !(title || moreLink) },
        className
      )}
    >
      <div className={classNames(styles.container)}>
        <CasinoHeaderTitle variant={titleVariant} title={title} subTitle={subTitle} />
        {hasNextButton ? (
          <div className={classNames(styles.buttonContainer)}>
            <StarsButton
              icon={<MoreIcon />}
              onClick={() => {
                onNextButtonClick?.();
                buttonPressed({ buttonName });
              }}
              variant="text"
              aria-label={buttonName}
              track={false}
            />
          </div>
        ) : null}
      </div>
      {(moreLink || showScroll) && (
        <div className={styles.buttons}>
          {moreLink && <CasinoAllButton href={moreLink} />}
          {showScroll && sliderRef && sliderState && (
            <CasinoSliderButtons sliderRef={sliderRef} sliderState={sliderState} />
          )}
        </div>
      )}
    </header>
  );
};

export default CasinoGameHeader;
